import React from 'react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="privacy-policy-page">
      <div className="max-w-4xl mx-auto px-6 sm:px-12 lg:px-24 py-12 sm:py-16">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1E3A8A] mb-4">Legal</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#0B1120] tracking-tight mb-4">Privacy Policy</h1>
        <p className="text-slate-500 mb-12">Last Updated: April 8, 2026</p>

        <div className="prose prose-slate max-w-none space-y-8 text-slate-700 leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">1. Introduction</h2>
            <p>
              Prestigious Travel Club Inc. ("we," "us," or "our") is committed to protecting your privacy and personal information.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website
              at prestigioustravelclub.org, use our services, or communicate with us.
            </p>
            <p className="mt-3">
              By accessing or using our website and services, you agree to this Privacy Policy. If you do not agree with the terms
              of this Privacy Policy, please do not access our website or use our services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">2. Company Information</h2>
            <p>
              <strong>Prestigious Travel Club Inc.</strong><br />
              1431 Simpson Road, #1067<br />
              Kissimmee, Florida 34744<br />
              Phone: 407-796-1937<br />
              Email: Info@prestigioustravelclub.org
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">3. Information We Collect</h2>
            <p>We may collect the following types of information:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Personal Information:</strong> Name, email address, phone number, mailing address, and other information you voluntarily provide through our contact forms, volunteer applications, membership inquiries, or donation processes.</li>
              <li><strong>Communication Data:</strong> Records of your communications with us, including emails, phone calls, SMS messages, and chat interactions with our AI assistant.</li>
              <li><strong>Usage Data:</strong> Information about how you use our website, including IP address, browser type, pages visited, time spent on pages, and referring URLs.</li>
              <li><strong>Cookies and Tracking Technologies:</strong> We use cookies, web beacons, and similar technologies to enhance your experience, analyze usage patterns, and improve our services. You may control cookie preferences through your browser settings.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">4. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Process membership inquiries and applications</li>
              <li>Respond to your questions, requests, and communications</li>
              <li>Send transactional messages related to your account or services</li>
              <li>Send promotional communications (with your consent)</li>
              <li>Process donations and issue tax receipts</li>
              <li>Improve our website, services, and user experience</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">5. SMS / Text Messaging Disclosure</h2>
            <p>
              By providing your phone number and opting in to receive SMS messages from Prestigious Travel Club Inc., you consent to
              receive text messages related to your inquiry, membership, services, and account communications. These messages may be
              sent using an automated telephone dialing system or prerecorded voice.
            </p>
            <p className="mt-3 font-semibold">
              No mobile information will be shared with third parties/affiliates for marketing/promotional purposes.
            </p>
            <p className="mt-3">
              Message frequency may vary. Message and data rates may apply. Consent to receive SMS messages is not a condition of
              purchase or membership.
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Opt-Out:</strong> You may opt out of receiving SMS messages at any time by replying <strong>STOP</strong> to any message you receive from us. After opting out, you will receive a one-time confirmation message and will no longer receive SMS messages from us.</li>
              <li><strong>Help:</strong> Reply <strong>HELP</strong> for help or contact us at Info@prestigioustravelclub.org or 407-796-1937.</li>
            </ul>
            <p className="mt-3">
              SMS messages are transmitted through Twilio, a third-party messaging platform. Twilio processes your phone number solely
              to deliver messages on our behalf and does not use your information for their own marketing purposes. For more information,
              see <a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] underline">Twilio's Privacy Policy</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">6. Email Communications</h2>
            <p>
              By providing your email address, you may receive communications from us including service-related emails, transaction
              confirmations, newsletters, and promotional materials. You may opt out of promotional emails at any time by clicking the
              "unsubscribe" link included in every email or by contacting us directly. Service-related and transactional emails are
              necessary for the operation of your account and may not be opted out of.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">7. Phone Communication Disclosure</h2>
            <p>
              By providing your phone number, you consent to receive phone calls from Prestigious Travel Club Inc. for purposes
              related to your inquiry, membership, or services. Calls may be made for appointment reminders, follow-ups on inquiries,
              and service-related communications. You may opt out of non-essential phone communications at any time by notifying us
              at Info@prestigioustravelclub.org.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">8. Cookies and Tracking Technologies</h2>
            <p>
              Our website uses cookies and similar tracking technologies to improve functionality and analyze usage. Types of cookies we use include:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Essential Cookies:</strong> Required for the website to function properly.</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website.</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences and settings.</li>
            </ul>
            <p className="mt-3">
              You can control cookie settings through your browser. Disabling certain cookies may limit your ability to use some features of our website.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">9. TCPA Compliance</h2>
            <p>
              Prestigious Travel Club Inc. is fully committed to compliance with the Telephone Consumer Protection Act (TCPA). We obtain
              proper prior express written consent before sending any automated text messages or making automated calls. Your consent is
              documented and stored securely. You have the right to revoke your consent at any time by:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Replying <strong>STOP</strong> to any SMS message</li>
              <li>Emailing us at Info@prestigioustravelclub.org</li>
              <li>Calling us at 407-796-1937</li>
              <li>Writing to us at 1431 Simpson Road, #1067, Kissimmee, FL 34744</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">10. Third-Party Service Providers</h2>
            <p>
              We may share your information with trusted third-party service providers who assist us in operating our website,
              conducting our business, and servicing you. These providers include:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Twilio:</strong> SMS and messaging delivery</li>
              <li><strong>Givebutter:</strong> Donation processing</li>
              <li><strong>SMTP2GO:</strong> Email delivery</li>
            </ul>
            <p className="mt-3">
              These third parties are contractually obligated to keep your information confidential and to use it only for the purposes
              for which we disclose it to them. We do not sell your personal information to any third party.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">11. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to fulfill the purposes described in this Privacy Policy,
              unless a longer retention period is required or permitted by law. When your information is no longer needed, we will
              securely delete or anonymize it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">12. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>The right to access the personal information we hold about you</li>
              <li>The right to request correction of inaccurate information</li>
              <li>The right to request deletion of your personal information</li>
              <li>The right to opt out of certain communications</li>
              <li>The right to withdraw consent for data processing</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, contact us at Info@prestigioustravelclub.org.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">13. Children's Privacy</h2>
            <p>
              Our website and services are not directed to individuals under the age of 13. We do not knowingly collect personal
              information from children under 13. If you believe we have collected information from a child under 13, please contact
              us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">14. Changes to This Privacy Policy</h2>
            <p>
              We reserve the right to update this Privacy Policy at any time. Changes will be posted on this page with an updated
              "Last Updated" date. Your continued use of our website after any changes constitutes your acceptance of the revised
              Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#0B1120] mb-3">15. Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <p className="mt-3">
              <strong>Prestigious Travel Club Inc.</strong><br />
              1431 Simpson Road, #1067<br />
              Kissimmee, Florida 34744<br />
              Phone: 407-796-1937<br />
              Email: Info@prestigioustravelclub.org
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
